import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Mail, Gift, Sparkles, CheckCircle, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { LanguageCode, MENU_UI_TRANSLATIONS } from '@/constants/menuUITranslations';

interface NewsletterSignupProps {
  restaurantId: string;
  restaurantName?: string;
  primaryColor?: string;
  currentLanguage?: LanguageCode;
}

const NewsletterSignup: React.FC<NewsletterSignupProps> = ({
  restaurantId,
  restaurantName,
  primaryColor = '#8B5CF6',
  currentLanguage = 'fr'
}) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [newsletterEnabled, setNewsletterEnabled] = useState<boolean | null>(null);
  const [restaurant, setRestaurant] = useState<any>(null);
  const t = MENU_UI_TRANSLATIONS[currentLanguage];

  useEffect(() => {
    const checkSettings = async () => {
      const { data } = await supabase
        .from('restaurants')
        .select('newsletter_enabled, welcome_email_message')
        .eq('id', restaurantId)
        .maybeSingle();
      
      setRestaurant(data);
      setNewsletterEnabled(data?.newsletter_enabled !== false);
    };
    checkSettings();
  }, [restaurantId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      toast.error(t.enterEmail);
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error(t.invalidEmail);
      return;
    }

    setLoading(true);
    try {
      const normalizedEmail = email.toLowerCase().trim();
      
      const { data: bouncedCheck } = await supabase
        .from('bounced_emails')
        .select('id')
        .eq('restaurant_id', restaurantId)
        .eq('email', normalizedEmail)
        .maybeSingle();
      
      if (bouncedCheck) {
        toast.error(t.invalidEmail);
        setLoading(false);
        return;
      }

      const { error } = await supabase
        .from('newsletter_subscribers')
        .upsert({
          restaurant_id: restaurantId,
          email: normalizedEmail,
          source: 'menu',
          is_active: true
        }, { onConflict: 'restaurant_id,email' });

      if (error) throw error;

      let customIntro = '';
      let customBenefits: string[] = [];
      if (restaurant?.welcome_email_message) {
        try {
          const parsed = JSON.parse(restaurant.welcome_email_message);
          customIntro = parsed.intro || '';
          customBenefits = parsed.benefits || [];
        } catch {
          // Ignore parsing errors
        }
      }

      if (restaurantName) {
        supabase.functions.invoke('send-welcome-newsletter', {
          body: {
            email: normalizedEmail,
            restaurant_id: restaurantId,
            restaurant_name: restaurantName,
            custom_intro: customIntro,
            custom_benefits: customBenefits
          }
        }).catch(err => console.error('Welcome email exception:', err));
      }

      setSubscribed(true);
      setEmail('');
      toast.success(t.subscriptionSuccess);
    } catch (error: any) {
      console.error('Newsletter subscription error:', error);
      if (error.code === '23505') {
        setSubscribed(true);
        toast.success(t.alreadySubscribed);
      } else {
        toast.error(t.errorOccurred);
      }
    } finally {
      setLoading(false);
    }
  };

  if (newsletterEnabled === null || newsletterEnabled === false) {
    return null;
  }

  if (subscribed) {
    return (
      <Card className="border-2 my-6 mx-4 lg:mx-0" style={{ borderColor: primaryColor }}>
        <CardContent className="py-6 text-center">
          <CheckCircle 
            className="w-12 h-12 mx-auto mb-3" 
            style={{ color: primaryColor }}
          />
          <h3 className="font-bold text-lg mb-1">{t.thanksForSubscribing}</h3>
          <p className="text-muted-foreground text-sm">
            {t.checkInbox}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-2 my-6 mx-4 lg:mx-0 overflow-hidden" style={{ borderColor: primaryColor }}>
      <div 
        className="py-3 px-4 text-white text-center"
        style={{ backgroundColor: primaryColor }}
      >
        <div className="flex items-center justify-center gap-2">
          <Sparkles className="w-5 h-5" />
          <span className="font-semibold">{t.dontMissAnything}</span>
          <Sparkles className="w-5 h-5" />
        </div>
      </div>
      <CardContent className="py-4">
        <div className="text-center mb-4">
          <p className="text-sm text-muted-foreground">
            {t.exclusiveOffers} <Gift className="w-4 h-4 inline text-primary" /> <strong>{t.gifts}</strong> !
          </p>
        </div>
        <form onSubmit={handleSubmit} className="flex gap-2">
          <div className="relative flex-1">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              type="email"
              placeholder={t.yourEmail}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="pl-10"
              disabled={loading}
            />
          </div>
          <Button 
            type="submit" 
            disabled={loading}
            style={{ backgroundColor: primaryColor }}
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              t.subscribe
            )}
          </Button>
        </form>
        <p className="text-xs text-muted-foreground text-center mt-3">
          {t.unsubscribeAnytime}
        </p>
      </CardContent>
    </Card>
  );
};

export default NewsletterSignup;
